// src/services/connection.ts
import api from "../lib/api";

// ============================================
// TYPE DEFINITIONS
// ============================================

export interface Connection {
  id: string;
  requesterId: string;
  receiverId: string;
  status: 'pending' | 'accepted' | 'rejected';
  message?: string;
  createdAt: string;
  respondedAt?: string;
  requester?: {
    id: string;
    firstName: string;
    lastName: string;
    title?: string;
    organizationName?: string;
    photoUrl?: string;
    userType: string;
  };
  receiver?: {
    id: string;
    firstName: string;
    lastName: string;
    title?: string;
    organizationName?: string;
    photoUrl?: string;
    userType: string;
  };
}

export interface ConnectionStatus {
  status: 'none' | 'pending' | 'accepted' | 'rejected' | 'self';
  connection?: {
    id: string;
    isRequester: boolean;
    createdAt: string;
    respondedAt?: string;
  };
}

export interface ConnectedUser {
  id: string;
  connectedAt: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    title?: string;
    organizationName?: string;
    photoUrl?: string;
    userType: string;
    email?: string;
  };
}

// ============================================
// CONNECTION MANAGEMENT ENDPOINTS
// ============================================

/**
 * Send a connection request to another user
 */
export async function sendConnectionRequest(receiverId: string, message?: string): Promise<Connection> {
  const { data } = await api.post('/api/connections/request', { receiverId, message });
  return data.data.connection;
}

/**
 * Get received connection requests (pending)
 */
export async function getReceivedRequests(): Promise<Connection[]> {
  const { data } = await api.get('/api/connections/received');
  return data.data.connections;
}

/**
 * Get sent connection requests (pending)
 */
export async function getSentRequests(): Promise<Connection[]> {
  const { data } = await api.get('/api/connections/sent');
  return data.data.connections;
}

/**
 * Get all accepted connections
 */
export async function getConnections(): Promise<ConnectedUser[]> {
  const { data } = await api.get('/api/connections');
  return data.data.connections;
}

/**
 * Accept a connection request
 */
export async function acceptConnection(connectionId: string): Promise<Connection> {
  const { data } = await api.patch(`/api/connections/${connectionId}/accept`);
  return data.data.connection;
}

/**
 * Reject a connection request
 */
export async function rejectConnection(connectionId: string): Promise<Connection> {
  const { data } = await api.patch(`/api/connections/${connectionId}/reject`);
  return data.data.connection;
}

/**
 * Cancel a sent connection request
 */
export async function cancelConnectionRequest(connectionId: string): Promise<void> {
  await api.delete(`/api/connections/${connectionId}/cancel`);
}

/**
 * Check connection status with another user
 */
export async function getConnectionStatus(userId: string): Promise<ConnectionStatus> {
  const { data } = await api.get(`/api/connections/status/${userId}`);
  return data.data;
}
